import { useState, useCallback, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useLocation } from 'react-router-dom';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';

import PageContent from '../../global/PageContent';
import { PaymentMethodForm, MembershipPaymentMethodConfirmation } from '../../global/StripeElements';
import { getGenericError } from '../../utils/errors';
import { createSetupIntent } from '../../utils/service';
import getStripePromise from '../../utils/stripe';
import {
  MEMBERSHIP_CONFIRM_PARAM,
  STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM,
  ROUTE_MANAGE_MEMBERSHIP,
} from '../../config/routes';
import { getSearchParam } from '../../utils/routes';
import { stripeAppearance, stripeFonts } from '../../config/stripe';

import './MembershipPaymentMethod.scss';

const stripePromise = getStripePromise();

const MembershipPaymentMethod = () => {
  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  const { search } = location;
  const query = new URLSearchParams(search);
  const confirm = getSearchParam(query, MEMBERSHIP_CONFIRM_PARAM);
  const setupIntentClientSecret = getSearchParam(query, STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM);

  const loadSetupIntent = useCallback(async () => {
    setLoading(true);

    const { data, error: setupIntentError } = await createSetupIntent();

    if (setupIntentError || !data.clientSecret) {
      setError(setupIntentError?.message || getGenericError());
      setClientSecret(null);
      setLoading(false);
      return;
    }

    setClientSecret(data.clientSecret);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!clientSecret) {
      loadSetupIntent();
    }
  }, [clientSecret]);

  return (
    <PageContent
      title="Change Membership Card"
      isLoading={loading}
      error={error}
      className="MembershipPaymentMethod"
      backButtonText="GO TO MY MEMBERSHIP"
      parentUrl={ROUTE_MANAGE_MEMBERSHIP}
    >
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: stripeAppearance,
          fonts: stripeFonts,
        }}
      >
        {confirm && setupIntentClientSecret ? (
          <MembershipPaymentMethodConfirmation setupIntentClientSecret={setupIntentClientSecret} />
        ) : (
          <PaymentMethodForm onCancel={() => navigateWithSearch(ROUTE_MANAGE_MEMBERSHIP)} />
        )}
      </Elements>
    </PageContent>
  );
};

export default MembershipPaymentMethod;
